import { Input } from '@cobliteam/ui/form';
import { useEffect, useRef, useState } from 'react';

import AlertFooter from '../components/AlertFooter';
import { CommandDialog, CommandInput } from '../components/Command';

import TagFeatures from '@cobliteam/ui-business/components/Tag/Features';
import ArticleDetails from '../components/Details/Article/ArticleDetails';
import ArticleIADetails from '../components/Details/ArticleIA/ArticleIADetails';
import DeviceDetails from '../components/Details/Device/DeviceDetails';
import DriverDetails from '../components/Details/Driver/DriverDetails';
import VehicleDetails from '../components/Details/Vehicle/VehicleDetails';
import VehicleGroupDetails from '../components/Details/VehicleGroup/VehicleGroupDetails';
import { ShineBorder } from '../components/ShineBorder';

import Sidebar from '../containers/Sidebar';
import { VirtualizedList } from '../containers/VirtualizedList';

import type { Preferences } from '@cobliteam/common-types';
import { useLocalStorage } from '@cobliteam/hooks';
import { Button, Tooltip } from '@cobliteam/ui/components';
import { useDebounce } from '@cobliteam/ui/hooks';
import Icon from '@cobliteam/ui/icons';
import { cn } from '@cobliteam/ui/utils';
import { trackGlobalSearchEvents } from '../helpers';
import { useKeyboardNavigation } from '../hooks/useKeyboardNavigation';
import useSearch from '../hooks/useSearch';
import { EnumCategory } from '../types';

interface GlobalSearchContentProps {
  handleUpdateData: () => void;
  isRefetching: boolean;
}

const GlobalSearchContent = ({ handleUpdateData, isRefetching }: GlobalSearchContentProps) => {
  const [preferences, setPreferences] = useLocalStorage<Preferences>('preferences');
  const searchRef = useRef<HTMLInputElement>(null);
  const [inputSearch, setInputSearch] = useState('');
  const debouncedSearch = useDebounce(inputSearch, 400);

  const { isOpen, selectedItem, setSearch, setIsOpen } = useSearch();

  // Add keyboard navigation
  useKeyboardNavigation();

  const isMac = /macintosh|mac os x/i.test(navigator.userAgent);

  const handleEscapeKeyDown = (e: Event) => {
    e.preventDefault();
    setSearch('');
    setInputSearch('');
    setIsOpen(false);
  };

  const onHandleUpdateData = () => {
    handleUpdateData();
    trackGlobalSearchEvents.updateData();
  };

  useEffect(() => {
    if (isOpen) {
      setSearch(debouncedSearch);
      if (debouncedSearch) trackGlobalSearchEvents.search(debouncedSearch);
    }

    return () => {
      if (!isOpen) {
        setSearch('');
        setInputSearch('');
        setPreferences({ ...preferences, globalSearchNew: false });
      }
    };
  }, [debouncedSearch, setSearch, isOpen]);

  return (
    <>
      <div className='relative flex items-center gap-2 rounded-lg border border-gray-4 '>
        {preferences?.globalSearchNew === false ? null : <ShineBorder shineColor={['#A07CFE', '#FE8FB5', '#FFBE7B']} borderWidth={1.5} />}
        <Input
          className='h-[34px] w-[326px] justify-between border-none bg-transparent'
          loading={false}
          disabled={false}
          autoFocus={isOpen}
          startIcon={<Icon name='search' />}
          placeholder='Procurar veículos, motoristas e etc...'
          onClick={() => {
            trackGlobalSearchEvents.openSearch();
            setIsOpen(true);
          }}
        />
        <kbd className='absolute right-2 inline-flex h-5 max-h-full items-center rounded border border-gray-5 bg-gray-2 px-1 font-[inherit] font-medium text-gray-a9 text-xs shadow-sm'>
          {isMac ? '⌘' : 'Ctrl'} + K
        </kbd>
        {preferences?.globalSearchNew === false ? null : <TagFeatures.New variant='contained' label='Novo' size='sm' className='-top-2 -right-2 absolute h-5 ' />}
      </div>
      <CommandDialog size='lg' open={isOpen} onOpenChange={setIsOpen} shouldFilter={false} onEscapeKeyDown={handleEscapeKeyDown}>
        <div className='flex items-center justify-between gap-2 border-gray-4 border-b pr-2'>
          <CommandInput ref={searchRef} autoFocus disabled={isRefetching} placeholder='Procurar veículos, motoristas e etc...' value={inputSearch} onValueChange={setInputSearch} />
          <Tooltip content='Atualizar dados da frota'>
            <Button variant='text' size='sm' onClick={onHandleUpdateData}>
              <Icon name='refresh-cw' className={cn(isRefetching && 'animate-spin')} />
            </Button>
          </Tooltip>
        </div>
        <div className='grid h-[500px] grid-cols-12'>
          <section className='col-span-3 border-gray-4 border-r'>
            <Sidebar />
          </section>
          <section className='col-span-4 overflow-auto border-gray-4 border-r'>
            <VirtualizedList />
          </section>
          <section className='col-span-5 flex gap-2'>
            {selectedItem?.category.id === EnumCategory.VEHICLES && <VehicleDetails />}
            {selectedItem?.category.id === EnumCategory.DRIVERS && <DriverDetails />}
            {selectedItem?.category.id === EnumCategory.DEVICES && <DeviceDetails />}
            {selectedItem?.category.id === EnumCategory.VEHICLE_GROUPS && <VehicleGroupDetails />}
            {selectedItem?.category.id === EnumCategory.ARTICLES && <ArticleDetails />}
            {selectedItem?.category.id === EnumCategory.IA_ARTICLES && <ArticleIADetails />}
          </section>
        </div>
        <AlertFooter />
      </CommandDialog>
    </>
  );
};

export default GlobalSearchContent;
